/**
 * Shared data contract + formatters for the Cari Hareketler exports.
 *
 * One contract is consumed by three places so they can never drift:
 *   - cari-movements-pdf.tsx        (the @react-pdf/renderer document)
 *   - export-cari-movements-xlsx.ts (the exceljs workbook)
 *   - the four pages that build `meta` and fetch the structured rows
 *
 * Two modes:
 *   - 'list'      : the global movement log (every cari), no running balance
 *   - 'statement' : a single cari's ledger (ekstre) with a running TL balance
 */
import type { ReportCompany } from './report-company';

export type MovementDocMode = 'list' | 'statement';

/**
 * One movement line. The running balances (`tryBalance` TL-equivalent,
 * `fxBalance` native-currency) are only populated in 'statement' mode; which
 * one the document shows is chosen per app via `MovementDocMeta.balanceNative`.
 */
export interface MovementDocRow {
  date: Date | string;
  cariCode: string;
  cariType: string;
  counterpartyName: string;
  voucherNo: string;
  description: string;
  currency: string;
  fxDebit: number;
  fxCredit: number;
  tryDebit: number;
  tryCredit: number;
  tryBalance?: number;
  fxBalance?: number;
  isManual: boolean;
}

/** Per-currency turnover totals (cross-currency sums are meaningless). */
export interface MovementCurrencyTotals {
  currency: string;
  fxDebit: number;
  fxCredit: number;
  fxNet: number;
  tryDebit: number;
  tryCredit: number;
  tryNet: number;
}

/** Counterparty header shown in 'statement' mode. */
export interface MovementStatementCari {
  code: string;
  name: string;
  type?: string | null;
}

export interface MovementDocData {
  rows: MovementDocRow[];
  totalsByCurrency: MovementCurrencyTotals[];
  cari?: MovementStatementCari;
}

export interface MovementDocColumns {
  no: string;
  date: string;
  cari: string;
  type: string;
  code: string;
  voucher: string;
  description: string;
  currency: string;
  fxDebit: string;
  fxCredit: string;
  tryDebit: string;
  tryCredit: string;
  balance: string;
}

export interface MovementDocMeta {
  mode: MovementDocMode;
  title: string; // "CARİ HAREKETLERİ" / "CARİ HESAP EKSTRESİ"
  periodLabel: string;
  generatedLabel: string; // "Oluşturulma: 08.06.2026 14:21"
  params: { label: string; value: string }[];
  columns: MovementDocColumns;
  totalLabel: string; // "TOPLAM"
  balanceLabel: string; // "BAKİYE"
  debitSide: string; // "(B)"
  creditSide: string; // "(A)"
  manualTag: string; // "M"
  emptyLabel: string; // "Hareket bulunamadı"
  pageLabel: (page: number, total: number) => string;
  currencyNames: Record<string, string>;
  // Statement running-balance basis: when true (travel), foreign-currency rows
  // show the native-currency balance (matching the screen); when false/omitted
  // (trading), every row shows the TL-equivalent balance under a "TL Bakiye"
  // header. Totals always use the native net regardless.
  balanceNative?: boolean;
}

/** Pick the running balance to display for a row, honouring `balanceNative`. */
export function rowBalance(row: MovementDocRow, balanceNative?: boolean): number {
  if (balanceNative && row.currency !== 'TRY') return row.fxBalance ?? 0;
  return row.tryBalance ?? 0;
}

export interface MovementDoc {
  company: ReportCompany;
  meta: MovementDocMeta;
  data: MovementDocData;
}

// ── Formatters ────────────────────────────────────────────────────────────────

const NF = new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Full number, always two decimals (used for totals). */
export const fmtNum = (n: number): string => NF.format(n);

/** Cell value — blank for ~zero so the table stays readable. */
export const fmtCell = (n: number): string => (Math.abs(n) < 0.005 ? '' : NF.format(n));

/** dd.MM.yyyy regardless of whether the value arrived as Date or ISO string. */
export function fmtDocDate(d: Date | string): string {
  const date = typeof d === 'string' ? new Date(d) : d;
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(date.getUTCDate())}.${p(date.getUTCMonth() + 1)}.${date.getUTCFullYear()}`;
}

const CURRENCY_ORDER = ['TRY', 'EUR', 'GBP', 'USD'];

/** Stable currency ordering for totals sections. */
export function sortCurrencyTotals(totals: MovementCurrencyTotals[]): MovementCurrencyTotals[] {
  return [...totals].sort(
    (a, b) =>
      ((CURRENCY_ORDER.indexOf(a.currency) + 100) % 100) - ((CURRENCY_ORDER.indexOf(b.currency) + 100) % 100) ||
      a.currency.localeCompare(b.currency)
  );
}
